<template>
  <WrapperForm
    v-model="currentTab"
    :tabs
    :customClass="['scrollable', 'scrollbar']"
  >
    <component :is="TABS_COMPONENTS[currentTab]" />
  </WrapperForm>
</template>

<script setup lang="ts">
  import { ref, type Component } from 'vue'
  import { storeToRefs } from 'pinia'
  import ApprentiContent from '@/features/interface/demande/capp/steps/apprenti/ApprentiStep.vue'
  import ContratContent from '@/features/interface/demande/capp/steps/contrat/ContratStep.vue'
  import DocumentsContent from '@/features/interface/demande/capp/steps/documents/DocumentsStep.vue'
  import EmployeurContent from '@/features/interface/demande/capp/steps/employeur/EmployeurStep.vue'
  import FormationContent from '@/features/interface/demande/capp/steps/formation/FormationStep.vue'
  import IntroductionContent from '@/features/interface/demande/capp/steps/introduction/IntroductionStep.vue'
  import SyntheseContent from '@/features/interface/demande/capp/steps/synthese/SyntheseStep.vue'
  import { useDemandeTabsSummarizer } from '@/features/interface/demande/shared'
  import type { ContratApprentissageDTO } from '@/features/api/extranet/dto/Demande/CAPP'
  import { type ErreurSummaryConfiguration } from '@/features/application/erreur-summary'
  import { useDemandeCAPPStore } from './useDemandeCAPPStore'

  const TabKeys = ['Introduction', 'Employeur', 'Apprenti', 'Contrat', 'Formation', 'Synthèse', 'Documents'] as const

  type TabKey = (typeof TabKeys)[number]

  const TABS_COMPONENTS: { [key in TabKey]?: Component } = {
    Introduction: IntroductionContent,
    Employeur: EmployeurContent,
    Apprenti: ApprentiContent,
    Contrat: ContratContent,
    Formation: FormationContent,
    Synthèse: SyntheseContent,
    Documents: DocumentsContent,
  } as const

  const currentTab = ref(TabKeys[0])

  const config = {
    Introduction: { include: (state) => [state.validationNotice] },
    Employeur: { includeDeep: (state) => [state.employeur] },
    Apprenti: { includeDeep: (state) => [state.apprenti, state.representantLegal, state.tutorat] },
    Contrat: { includeDeep: (state) => [state.contrat, state.remuneration] },
    Formation: { includeDeep: (state) => [state.formation] },
    Synthèse: {
      include: (state) => [state.lieuSignature, state.commentaire, state.acceptationCg, state.attestationSurHonneur],
    },
    Documents: {
      includeDeep: (state) => [state.fichiers],
      include: (state) => [state.attestationPiecesJustificatives],
    },
  } satisfies ErreurSummaryConfiguration<ContratApprentissageDTO>

  const { meta } = storeToRefs(useDemandeCAPPStore())

  const { tabs } = useDemandeTabsSummarizer<ContratApprentissageDTO>(config, meta)
</script>
